// Weight-item units for the native purchasing screens — THE ONE PLACE the
// pound/ounce conversion lives. Nothing else in nativepurchasing/ may multiply
// or divide by 16.
//
// THE CONVENTION (verified against the mirror 8/11/26, do not "simplify"):
//   Fragrance oils and wax are in NetSuite's Weight units type, whose BASE UNIT
//   IS OUNCES. Every stored quantity is in base ounces and every stored rate is
//   per base ounce — purchase_order_lines.quantity_ordered / unit_cost,
//   inventory_items.current_cost / average_cost, transactionline.quantity,
//   totalquantityonhand, inventorybalance. The DISPLAY unit everyone actually
//   buys, sells and talks in is POUNDS.
//
//   Evidence (12 historical Cosmo International Fragrances POs, purchase_orders
//   + purchase_order_lines): every quantity is an exact multiple of 16 —
//   400/560/640/800/1200/1600/2400 oz = 25/35/40/50/75/100/150 lb — and every
//   unit_cost is a per-pound price over 16: 0.9375 = $15.00/lb, 1.00 = $16.00,
//   1.21875 = $19.50, 1.25 = $20.00, 1.28125 = $20.50, 0.53125 = $8.50. The
//   smallest quantity ever ordered, 400 oz, is exactly Cosmo's stated MOQ of
//   25 lb (acct_vendors.ns_raw.comments: "MOQ 25 lbs"). The item master agrees:
//   OIL-* rows carry units_type '1' with weight_unit "per lb." and
//   current_cost 1.2188 (= $19.50/lb).
//
//   Note that purchase_uom_id === stock_uom_id on these items: there is NO
//   purchase-to-stock conversion. The lb -> oz conversion happens ONCE, at
//   ORDER ENTRY (here, and in oilOrderingRoutes for the oil dashboard). What is
//   stored on the line already IS the stock quantity, which is why native
//   receiving deliberately refuses to divide by 16 on the way in.
//
// So: this module converts at the DISPLAY boundary only. State and inputs on
// the PO form hold pounds and dollars-per-pound; the API contract is unchanged
// and still speaks base ounces, exactly like every mirrored row.

export const OZ_PER_LB = 16;

// NetSuite's units-type internal id for the Weight type (base unit ounces).
// Mirrors nativeReceiptsRoutes.isWeightUomItem, which labels the same lines on
// the receiving screen.
const NS_WEIGHT_UNITS_TYPE_ID = '1';
// House SKU convention (packages/catalog parseSku, oilOrderingRoutes): OIL-…
// fragrance oils, Wax-… wax.
const WEIGHT_SKU_RE = /^(oil|wax)[-_\s]/i;

/** True when an item from /api/native-items is bought and stocked by weight. */
export function isWeightItem(item) {
  if (!item) return false;
  const unitsType = String(item.unitsType == null ? '' : item.unitsType).trim();
  if (unitsType === NS_WEIGHT_UNITS_TYPE_ID || /weight/i.test(unitsType)) return true;
  return WEIGHT_SKU_RE.test(String(item.sku || '').trim());
}

/**
 * True when an existing purchase_order_lines row is a weight line — used when
 * the form loads a PO to edit or copy. `unit_of_measure` is what this screen
 * and oilOrderingRoutes write; NS-mirror rows leave it NULL and carry only the
 * SKU, hence the prefix fallback.
 */
export function isWeightLine(line) {
  if (!line) return false;
  const uom = String(line.unit_of_measure ?? line.unitOfMeasure ?? '').trim();
  if (/^(oz|ounce|ounces)$/i.test(uom)) return true;
  if (line.is_oil_item === true) return true;
  return WEIGHT_SKU_RE.test(String(line.item_sku ?? line.sku ?? '').trim());
}

// Both directions are exact in binary floating point (16 is a power of two),
// so a value can round-trip display -> storage -> display without drift.

/** Display pounds -> stored base ounces. */
export const poundsToBaseOunces = (lb) => (Number(lb) || 0) * OZ_PER_LB;

/** Stored base ounces -> display pounds. */
export const baseOuncesToPounds = (oz) => (Number(oz) || 0) / OZ_PER_LB;

/** Display dollars-per-pound -> stored cost per base ounce. */
export const costPerPoundToBase = (perLb) => (Number(perLb) || 0) / OZ_PER_LB;

/** Stored cost per base ounce -> display dollars-per-pound. */
export const costPerBaseToPound = (perOz) => (Number(perOz) || 0) * OZ_PER_LB;

/** Quantity unit shown on a line: "lbs" for weight items, "ea" otherwise. */
export const quantityUnit = (isWeight) => (isWeight ? 'lbs' : 'ea');

/** Rate unit shown on a line: "/lb" for weight items, "/ea" otherwise. */
export const costUnit = (isWeight) => (isWeight ? '/lb' : '/ea');

/** "350 lbs" / "12 ea" — the quantity as it should read on screen and in print. */
export function formatQuantity(quantity, isWeight) {
  const n = Number(quantity) || 0;
  // Pounds can be fractional (a 12.5 lb top-off); eaches never are.
  const shown = Number.isInteger(n) ? String(n) : String(Number(n.toFixed(4)));
  return `${shown} ${quantityUnit(isWeight)}`;
}
